import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QrCode, Users, Zap } from 'lucide-react';

export default function FloatingActionButtons() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(true);

    const isAttendancePage = location.pathname === '/attendance';

    useEffect(() => {
        const checkForModals = () => {
            // Check if any modal-like element exists in the DOM
            // Look for fixed elements with high z-index that are not our own containers
            const modals = document.querySelectorAll('.fixed.inset-0');
            const isModalOpen = Array.from(modals).some(modal => {
                const style = window.getComputedStyle(modal);
                return (parseInt(style.zIndex) >= 50) && style.display !== 'none';
            });

            setIsVisible(!isModalOpen);
        };

        const observer = new MutationObserver(checkForModals);
        observer.observe(document.body, { childList: true, subtree: true });

        const handleToggle = (e: any) => {
            if (e.detail && typeof e.detail.visible === 'boolean') {
                setIsVisible(e.detail.visible);
            }
        };

        window.addEventListener('toggleQuickActions', handleToggle);

        // Initial check
        checkForModals();

        return () => {
            window.removeEventListener('toggleQuickActions', handleToggle);
            observer.disconnect();
        };
    }, []);

    if (!isVisible || isAttendancePage) return null;

    return (
        <>
            {/* Desktop & Tablet - Minimizable Dock */}
            <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-6 lg:bottom-10 flex-col items-center z-50 group">
                {/* Expanded Actions */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 flex flex-col gap-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 w-max">
                    <button
                        onClick={() => navigate('/attendance')}
                        className="flex items-center gap-3 bg-white pl-4 pr-3 py-2 rounded-full shadow-xl hover:shadow-2xl border border-zinc-100 transition-all hover:scale-105 whitespace-nowrap"
                    >
                        <span className="text-xs font-bold text-zinc-700">Scan Attendance</span>
                        <div className="bg-[#AAD816]/10 text-[#AAD816] p-1.5 rounded-full">
                            <QrCode className="w-4 h-4" />
                        </div>
                    </button>

                    <button
                        onClick={() => navigate('/members')}
                        className="flex items-center gap-3 bg-white pl-4 pr-3 py-2 rounded-full shadow-xl hover:shadow-2xl border border-zinc-100 transition-all hover:scale-105 whitespace-nowrap"
                    >
                        <span className="text-xs font-bold text-zinc-700">Search Members</span>
                        <div className="bg-[#AAD816]/10 text-[#AAD816] p-1.5 rounded-full">
                            <Users className="w-4 h-4" />
                        </div>
                    </button>
                </div>

                {/* Main Flash Icon */}
                <div className="w-10 h-10 bg-zinc-950 rounded-full shadow-xl shadow-zinc-200 flex items-center justify-center text-[#AAD816] transition-all duration-300 transform group-hover:scale-110 cursor-pointer border-2 border-zinc-900">
                    <Zap className="w-5 h-5 fill-current" />
                </div>
            </div>

            {/* Mobile - Modern Floating Pill Navigation */}
            <div className="md:hidden absolute bottom-6 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-sm">
                <div className="bg-zinc-950/95 backdrop-blur-xl border border-zinc-800 rounded-3xl shadow-2xl p-2 flex items-center justify-between gap-1 overflow-hidden">
                    <button
                        onClick={() => navigate('/attendance')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl active:bg-white/5 transition-all active:scale-95 text-white"
                    >
                        <div className="bg-[#AAD816]/10 p-2 rounded-xl">
                            <QrCode className="w-5 h-5 text-[#AAD816]" />
                        </div>
                        <span className="text-xs font-black uppercase tracking-widest">Scan</span>
                    </button>

                    <div className="w-[1px] h-8 bg-zinc-800" />

                    <button
                        onClick={() => navigate('/members')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl active:bg-white/5 transition-all active:scale-95 text-white"
                    >
                        <div className="bg-[#AAD816]/10 p-2 rounded-xl">
                            <Users className="w-5 h-5 text-[#AAD816]" />
                        </div>
                        <span className="text-xs font-black uppercase tracking-widest">Members</span>
                    </button>
                </div>
            </div>
        </>
    );
}
